import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Sync as SyncIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { InfoTip } from '../LabelWithInfo';

const emptyForm = { name: '', area: '', statusActive: true };

/**
 * Catálogo de cargos ERP (PLAN_ERP.md §4.4). Separado de positions (ATS):
 * este es el catálogo para cotizar. La spec exige poder crear el cargo antes
 * de cotizar cuando no existe (caso borde 4.2).
 */
const ErpJobTitlesTab = () => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert, user } = useStore.getState();
  // "Sincronizar con Talana" a demanda: solo super-admin (decisión Erwin 2026-09-24)
  const isSuperAdmin = user?.rol === 'super-admin';
  const [syncing, setSyncing] = useState(null); // uid del cargo en sincronización

  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);


  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/erp/job-titles', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response?.status_response === 'OK') {
        setJobTitles(response.data.response.data || []);
      } else {
        setError(t('erp.jobTitles.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.jobTitles.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Asegura el cargo en Talana (busca por nombre en el catálogo; si no existe lo crea)
  const syncTalana = async (jobTitle) => {
    try {
      setSyncing(jobTitle.uid);
      const response = await api.post(`/erp/job-titles/${jobTitle.uid}/talana/sync`,
        { organizationId: organization }, { headers: { Authorization: `Bearer ${authToken}` } });
      const body = response.data.response;
      const ref = body?.data;
      if (body?.status_response === 'OK') {
        showAlert(t(ref?.status === 'created' ? 'erp.jobTitles.talana.created' : 'erp.jobTitles.talana.linked', { id: ref?.id }), 'success');
      } else {
        showAlert(ref?.lastError?.message || t('erp.jobTitles.talana.error'), 'error');
      }
      load();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 503) showAlert(t('erp.talana.notConfigured'), 'warning');
      else showAlert(t('erp.jobTitles.talana.error'), 'error');
    } finally {
      setSyncing(null);
    }
  };

  const talanaChip = (jobTitle) => {
    const ref = jobTitle.externalRefs?.talana;
    if (!ref) {
      return (
        <Tooltip title={t('erp.jobTitles.talana.tipNone')}>
          <Chip size="small" variant="outlined" label={t('erp.clients.talana.none')} />
        </Tooltip>
      );
    }
    if (ref.status === 'linked' || ref.status === 'created') {
      return (
        <Tooltip title={t('erp.jobTitles.talana.tipLinked', {
          name: ref.name || '', id: ref.id,
          method: ref.method ? t(`erp.jobTitles.talana.method.${ref.method}`) : '',
        })}>
          <Chip size="small" color="success" variant="outlined" label={`Talana · ${ref.id}`} />
        </Tooltip>
      );
    }
    if (ref.status === 'failed') {
      return (
        <Tooltip title={t('erp.jobTitles.talana.tipFailed', { error: ref.lastError?.message || '' })}>
          <Chip size="small" color="error" variant="outlined" label={t('erp.clients.talana.failed')} />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={t('erp.jobTitles.talana.tipPending')}>
        <Chip size="small" variant="outlined" label={t('erp.clients.talana.pending')} />
      </Tooltip>
    );
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (jobTitle) => {
    setEditing(jobTitle.uid);
    setForm({
      name: jobTitle.name || '',
      area: jobTitle.area || '',
      statusActive: jobTitle.status === 'active',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }

    const payload = {
      organizationId: organization,
      name: form.name.trim(),
      area: form.area || null,
      status: form.statusActive ? 'active' : 'inactive',
    };

    try {
      setSaving(true);

      const response = editing
        ? await api.put(`/erp/job-titles/${editing}`, payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
        : await api.post('/erp/job-titles', payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          });

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.jobTitles.saveSuccess'), 'success');
        setDialogOpen(false);
        load();
      } else {
        showAlert(t('erp.jobTitles.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      if (message.includes('already exists')) {
        showAlert(t('erp.jobTitles.duplicateName'), 'error');
      } else {
        showAlert(t('erp.jobTitles.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>{t('erp.jobTitles.title')}</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
        >
          {t('erp.jobTitles.add')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : jobTitles.length === 0 ? (
        <Alert severity="info">{t('erp.jobTitles.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.jobTitles.table.name')}</TableCell>
                <TableCell>{t('erp.jobTitles.table.area')}</TableCell>
                <TableCell>{t('erp.jobTitles.table.status')}</TableCell>
                <TableCell>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    {t('erp.clients.table.talana')}
                    <InfoTip title={t('erp.jobTitles.talana.columnHelp')} />
                  </Box>
                </TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobTitles.map((jobTitle) => (
                <TableRow key={jobTitle.uid} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {jobTitle.name}
                    {jobTitle.source === 'ats' && (
                      <Chip size="small" variant="outlined" label={t('erp.jobTitles.sourceAts')}
                        sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{jobTitle.area || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={jobTitle.status === 'active'
                        ? t('erp.common.active')
                        : t('erp.common.inactive')}
                      color={jobTitle.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{talanaChip(jobTitle)}</TableCell>
                  <TableCell align="right">
                    {isSuperAdmin && (
                      <Tooltip title={t('erp.jobTitles.talana.sync')}>
                        <span>
                          <IconButton size="small" disabled={syncing === jobTitle.uid} onClick={() => syncTalana(jobTitle)}>
                            {syncing === jobTitle.uid ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={t('erp.jobTitles.edit')}>
                      <IconButton size="small" onClick={() => openEdit(jobTitle)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editing ? t('erp.jobTitles.edit') : t('erp.jobTitles.add')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField
            fullWidth
            size="small"
            required
            label={t('erp.jobTitles.form.name')}
            helperText={t('erp.tooltips.jobTitle.name')}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            size="small"
            label={t('erp.jobTitles.form.area')}
            value={form.area}
            onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))}
          />
          {editing && (
            <FormControlLabel
              control={(
                <Switch
                  checked={form.statusActive}
                  onChange={(e) => setForm((p) => ({ ...p, statusActive: e.target.checked }))}
                />
              )}
              label={form.statusActive ? t('erp.common.active') : t('erp.common.inactive')}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
          >
            {saving ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpJobTitlesTab;
